import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
} from 'react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { xcss, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsDismissed } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import {
	ConditionalNudgeWrapper,
	type ConditionalNudgeWrapperProps,
} from '@atlassian/jira-software-onboarding-nudges--next/src/ui/conditional-nudge-wrapper/index.tsx';
import { BacklogAccordionNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-accordion/async.tsx';
import { BACKLOG, SPRINT } from '../../../model/constants.tsx';
import { useCardListContext } from '../../../services/card-list-context/index.tsx';
import { BacklogButtonVariant } from '../../whiteboards-entrypoint-experiment/backlog-button-variant/view.tsx';
import { WhiteboardsAllJiras } from '../../whiteboards-entrypoint-experiment/expand-to-all-jiras/index.tsx';
import { CardListCheckbox } from '../cardlist-checkbox/index.tsx';
import { CardListAccordionButton } from './card-list-accordion-button/index.tsx';
import { CreateSprintButton } from './create-sprint-button/index.tsx';
import messages from './messages.tsx';
import { SprintHeader } from './sprint-header/index.tsx';

const BacklogAccordionNudgeAsyncWithFullWidth = ({
	children,
}: JSX.LibraryManagedAttributes<
	ConditionalNudgeWrapperProps['Wrapper'],
	ComponentProps<ConditionalNudgeWrapperProps['Wrapper']>
>) => (
	<Flex xcss={backlogAccordionNudgeAsyncStyles}>
		<BacklogAccordionNudgeAsync>{children}</BacklogAccordionNudgeAsync>
	</Flex>
);

export interface CardListHeaderProps {
	isCreateSprintButtonVisible: boolean;
	isDisabled: boolean;
	isMaxLimitReached: boolean;
	isMinLimitReached: boolean;
	onCreateSprint: (analyticsEvent: UIAnalyticsEvent) => void;
	onToggle?: () => void;
}

export const CardListHeader = ({
	isCreateSprintButtonVisible,
	isDisabled,
	isMaxLimitReached,
	isMinLimitReached,
	onCreateSprint,
	onToggle = noop,
}: CardListHeaderProps) => {
	const { cardListId, type, listName } = useCardListContext();
	const { formatMessage } = useIntl();
	const [isOnboardingDismissed] = useIsDismissed();

	return (
		<Flex
			xcss={[
				cardListHeaderStyles,
				isMaxLimitReached && maxLimitReachedStyles,
				isMinLimitReached && minLimitReachedStyles,
			]}
		>
			<Flex alignItems="center">
				<CardListCheckbox
					cardListId={cardListId}
					isDisabled={isDisabled}
					listName={`${type === SPRINT ? `${formatMessage(messages.sprintListNamePrefix)} ` : ''}${listName}`}
				/>
				<ConditionalNudgeWrapper
					Wrapper={BacklogAccordionNudgeAsyncWithFullWidth}
					conditionsToApplyWrapper={!isOnboardingDismissed && type === BACKLOG}
				>
					<CardListAccordionButton onToggle={onToggle} />
				</ConditionalNudgeWrapper>
			</Flex>

			<Flex alignItems="center" gap="space.100">
				<SprintHeader />

				{type === BACKLOG && <BacklogButtonVariant />}
				{type === BACKLOG && fg('grw_1037_expand_plan_in_whiteboards_gate') && (
					<WhiteboardsAllJiras />
				)}

				{isCreateSprintButtonVisible && <CreateSprintButton onCreateSprint={onCreateSprint} />}
			</Flex>
		</Flex>
	);
};

const cardListHeaderStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 'space.100',
	minHeight: token('space.400'),
	paddingInline: 'space.100',
	// Added the 1px border so that we get 9px of padding on the left and right of the header.
	// We do this so that the checkbox aligns with the checkboxes in the card list.
	borderLeft: '1px solid transparent',
	borderRight: '1px solid transparent',
});

const minLimitReachedStyles = xcss({
	backgroundColor: 'color.background.accent.yellow.subtlest',
});

const maxLimitReachedStyles = xcss({
	backgroundColor: 'color.background.accent.red.subtlest',
});

const backlogAccordionNudgeAsyncStyles = xcss({
	flex: '1',
});
